/* ════════════════════════════════════════════════
   OSI PAGES — page-specific animations
   Loaded on press, partners, contact, all-products,
   blog (recipes), home (panels), and any section that
   renders the osi-pages-assets snippet.

   Provides:
   • Scroll-reveal for .osi-reveal elements
   • Random wandering for .osi-contact__deco-img
   • Horizontal pin-and-scroll for [data-osi-pin-wrap]

   Hard idempotency: if loaded twice, only first wires up.
   ════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (window.__osiPagesInit) return;
  window.__osiPagesInit = true;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function showAll() {
    var nodes = document.querySelectorAll('.osi-reveal');
    nodes.forEach(function (n) { n.classList.add('is-visible'); });
  }

  var safetyTimer = setTimeout(showAll, 1500);

  function init() {
    setupReveal();
    setupContactDeco();
    setupHorizontalScroll();
  }

  function waitForGsap(callback) {
    var attempts = 0;
    function check() {
      if (window.gsap && window.ScrollTrigger) { callback(true); return; }
      if (attempts++ > 50) { callback(false); return; }
      setTimeout(check, 50);
    }
    check();
  }

  /* ── Scroll-reveal ── */
  function setupReveal() {
    var nodes = document.querySelectorAll('.osi-reveal');
    if (!nodes.length) { clearTimeout(safetyTimer); return; }

    if (prefersReducedMotion) {
      clearTimeout(safetyTimer);
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }

    waitForGsap(function (gsapReady) {
      clearTimeout(safetyTimer);

      if (gsapReady) {
        try { window.gsap.registerPlugin(window.ScrollTrigger); } catch (e) {}
        nodes.forEach(function (node, i) {
          window.gsap.fromTo(
            node,
            { autoAlpha: 0, y: 40 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.7,
              delay: (i % 6) * 0.05,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: node,
                start: 'top 92%',
                toggleActions: 'play none none none'
              },
              onStart: function () { node.classList.add('is-visible'); }
            }
          );
        });
        return;
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              io.unobserve(entry.target);
            }
          });
        }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
        nodes.forEach(function (n) { io.observe(n); });
      } else {
        nodes.forEach(function (n) { n.classList.add('is-visible'); });
      }
    });
  }

  /* ── Contact: random wandering floating images ──
     Each image picks a random destination inside its
     parent box (.osi-contact__deco), tweens there with
     random duration & ease, and on complete picks the
     next random destination. Result: continuous organic
     drifting. The parent has overflow:hidden so any
     image that wanders past the edge clips cleanly.

     Drift bounds: ±90px x / ±70px y / ±14° rotation.
     Combined with each image's CSS-anchored top/left
     base position, the visual range covers most of
     the box without images all collapsing to center. */
  function setupContactDeco() {
    var deco = document.querySelectorAll('.osi-contact__deco-img');
    if (!deco.length || prefersReducedMotion) return;

    waitForGsap(function (gsapReady) {
      if (gsapReady) {
        var rand = window.gsap.utils.random;

        function wander(img) {
          window.gsap.to(img, {
            x: rand(-90, 90),
            y: rand(-70, 70),
            rotation: rand(-14, 14),
            duration: rand(5, 9),
            ease: 'sine.inOut',
            onComplete: function () { wander(img); }
          });
        }

        deco.forEach(function (img) {
          /* Seed each image at a different random offset so
             they don't all start in lockstep. */
          window.gsap.set(img, {
            x: rand(-40, 40),
            y: rand(-30, 30),
            rotation: rand(-10, 10)
          });
          /* Stagger the first wander tween so they desync. */
          window.gsap.delayedCall(rand(0, 1.8), function () { wander(img); });
        });
        return;
      }

      /* GSAP fallback: simple CSS keyframe float so the
         images aren't completely static. */
      deco.forEach(function (img, i) {
        img.style.animation = 'osi-float ' + (3 + (i % 4) * 0.5) + 's ease-in-out ' + (i * 0.2) + 's infinite alternate';
      });
      if (!document.getElementById('osi-float-keyframes')) {
        var style = document.createElement('style');
        style.id = 'osi-float-keyframes';
        style.textContent = '@keyframes osi-float { 0% { transform: translateY(0) rotate(0); } 100% { transform: translateY(-18px) rotate(5deg); } }';
        document.head.appendChild(style);
      }
    });
  }

  /* ── Horizontal pin-and-scroll ── */
  function setupHorizontalScroll() {
    if (prefersReducedMotion) return;

    var wraps = document.querySelectorAll('[data-osi-pin-wrap]');
    if (!wraps.length) return;

    waitForGsap(function (gsapReady) {
      if (!gsapReady) {
        if (window.console && console.warn) {
          console.warn('osi-pages: GSAP failed to load — horizontal pin disabled.');
        }
        return;
      }

      try { window.gsap.registerPlugin(window.ScrollTrigger); } catch (e) {}

      var ST = window.ScrollTrigger;

      if (window.matchMedia('(max-width: 749px)').matches && typeof ST.normalizeScroll === 'function') {
        ST.normalizeScroll(true);
      }

      wraps.forEach(function (wrap) {
        var track = wrap.querySelector('[data-osi-pin-track]');
        if (!track || track.children.length < 2) return;

        if (wrap.dataset.osiPinReady === '1') return;
        wrap.dataset.osiPinReady = '1';

        var distance = function () {
          return Math.max(0, track.scrollWidth - wrap.offsetWidth);
        };

        window.gsap.to(track, {
          x: function () { return -distance(); },
          ease: 'none',
          scrollTrigger: {
            trigger: wrap,
            pin: true,
            pinSpacing: true,
            scrub: 1,
            start: 'top top',
            end: function () { return '+=' + distance(); },
            invalidateOnRefresh: true,
            anticipatePin: 1
          }
        });
      });

      var doRefresh = function () {
        if (ST && typeof ST.refresh === 'function') ST.refresh();
      };
      setTimeout(doRefresh, 300);
      if (document.readyState !== 'complete') {
        window.addEventListener('load', doRefresh, { once: true });
      }
      if (document.fonts && typeof document.fonts.ready === 'object' && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(doRefresh).catch(function () {});
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
